import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  useDeleteHealthCheck,
  useProbeNow,
  useUpdateHealthCheck,
  type HealthCheck,
} from '@/lib/health-checks'
import { relative } from '@/lib/format'

interface Props {
  check: HealthCheck
  onEdit: (c: HealthCheck) => void
}

function statusDot(c: HealthCheck): { color: string; label: string } {
  if (c.auto_disabled_at) return { color: 'bg-amber-500', label: 'auto-disabled' }
  if (!c.enabled) return { color: 'bg-muted-foreground', label: 'disabled' }
  if (c.last_status == null) return { color: 'bg-muted-foreground', label: 'no probe yet' }
  const ok = c.last_status >= c.expect_min && c.last_status <= c.expect_max
  return ok
    ? { color: 'bg-emerald-500', label: 'up' }
    : { color: 'bg-destructive', label: 'down' }
}

export function HealthCheckCard({ check, onEdit }: Props) {
  const probe = useProbeNow()
  const upd = useUpdateHealthCheck()
  const del = useDeleteHealthCheck()
  const dot = statusDot(check)

  return (
    <Card>
      <CardContent className="pt-4 space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`inline-block h-2 w-2 rounded-full ${dot.color}`} />
            <span className="font-medium truncate">{check.service_name}</span>
            <span className="text-muted-foreground">/ {check.name}</span>
            <Badge variant="outline" className="text-[10px]">{dot.label}</Badge>
          </div>
        </div>
        <div className="text-xs text-muted-foreground font-mono">
          {check.method} http://{check.service_name}{check.port != null ? `:${check.port}` : ''}
          {check.url_path}
        </div>
        {check.auth_header_name && (
          <div className="text-xs">
            Auth: {check.auth_header_name} {check.auth_header_set ? '(set)' : '(empty)'}
          </div>
        )}
        <div className="text-xs text-muted-foreground">
          Last:{' '}
          {check.last_status != null
            ? `HTTP ${check.last_status}`
            : (check.last_error_text ?? '—')}
          {' · '}
          {check.last_probe_at ? relative(check.last_probe_at) : 'never'}
        </div>
        <div className="flex flex-wrap gap-1">
          <Button size="sm" variant="outline" onClick={() => onEdit(check)}>Edit</Button>
          <Button
            size="sm"
            variant="outline"
            disabled={probe.isPending}
            onClick={() => probe.mutate(check.id)}
          >Probe now</Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => upd.mutate({ id: check.id, body: { enabled: !check.enabled } })}
          >{check.enabled ? 'Disable' : 'Enable'}</Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive"
            onClick={() => {
              if (confirm(`Delete health check ${check.service_name}/${check.name}?`)) {
                del.mutate(check.id)
              }
            }}
          >Delete</Button>
        </div>
      </CardContent>
    </Card>
  )
}
